import { HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInRearHeadOnPhotoDto, vehicleCheckInRearHeadOnPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface RearHeadOnPhotoDialogProps {
  initialValues: VehicleCheckInRearHeadOnPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInRearHeadOnPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function RearHeadOnPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: RearHeadOnPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInRearHeadOnPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Rear Head On Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Rear Head On'
                      file={values.vehicleCheckInRearHeadOnPhotoFile}
                      placeholderSrc='/17 Rear head on.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='rear-head-on-browse'
                    cameraInputId='rear-head-on-camera'
                    disabled={isSubmitting || isLoading}
                    onFileChosen={file => setFieldValue('vehicleCheckInRearHeadOnPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Rear Head On Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || Pressing “Previous” goes to the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
